import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {MetricsExplorerSchema} from "@/lib/types/metricsExplorer.ts";
import {ExplorerConfig, ExplorerSeriesConfig} from "@/pages/metricsExplorer/explorerConfig.ts";
import {
  availableDataTypes,
  availableMetrics,
  findDataType,
  uniqueCheckTypes,
} from "@/pages/metricsExplorer/schemaUtils.ts";

/**
 * Fill in whatever the catalog can decide for itself.
 *
 * A series that names checks but no data type or metric gets the first of each its check
 * type offers. That covers two cases: a user who has just picked a check and should see a
 * chart rather than more empty dropdowns, and a preset link that deliberately leaves these
 * blank so the frontend never has to keep its own copy of which tables a check writes to.
 *
 * Returns the original object unchanged when there is nothing to fill, so a caller can use
 * identity to decide whether to write back.
 */
export const applyCatalogDefaults = (
  config: ExplorerConfig,
  schema: MetricsExplorerSchema | undefined,
  checkMeta: Record<number, CheckInfo>
): ExplorerConfig => {
  if (!schema) {
    return config;
  }
  let changed = false;
  const series = config.series.map((entry) => {
    const filled = fillSeries(entry, schema, checkMeta);
    if (filled !== entry) {
      changed = true;
    }
    return filled;
  });
  return changed ? {...config, series} : config;
};

const fillSeries = (
  series: ExplorerSeriesConfig,
  schema: MetricsExplorerSchema,
  checkMeta: Record<number, CheckInfo>
): ExplorerSeriesConfig => {
  if (series.checkIds.length === 0) {
    return series;
  }
  const checkTypes = uniqueCheckTypes(
    series.checkIds.map((id) => checkMeta[id]?.monitoring_service_type).filter((type): type is string => !!type)
  );
  // The check types are not known until the pickers have resolved them; leave the series
  // alone rather than guessing from an incomplete picture.
  if (checkTypes.length !== new Set(series.checkIds).size && checkTypes.length === 0) {
    return series;
  }
  const dataTypeName = series.dataType || availableDataTypes(schema, checkTypes)[0]?.name;
  if (!dataTypeName) {
    return series;
  }
  const dataType = findDataType(schema, checkTypes, dataTypeName);
  const [firstMetric] = availableMetrics(dataType);
  const needsDataType = dataTypeName !== series.dataType;
  // Compare on content, not identity: a data type with nothing to offer would otherwise
  // hand back a fresh empty array every pass and the caller would write forever.
  const needsMetric = series.metrics.length === 0 && firstMetric !== undefined;
  if (!needsDataType && !needsMetric) {
    return series;
  }
  return {
    ...series,
    dataType: dataTypeName,
    metrics: needsMetric ? [firstMetric.name] : series.metrics,
  };
};
